/**
 *  @swagger
 *  definitions:
 *    RegattaEntryParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        RegattaGroupId:
 *          description: ID of the associated RegattaGroup
 *          type: string
 *        EntryId:
 *          description: ID of the associated Entry
 *          type: string
 *        Uid:
 *          description: Uid of the RegattaEntry
 *          type: string
 *        BowNo:
 *          description: Bow No of the RegattaEntry
 *          type: integer
 *        Name:
 *          description: Name of the RegattaEntry
 *          type: string
 *        Image:
 *          description: Image of the RegattaEntry
 *          type: string
 *        RegistrationNo:
 *          description: Registration No of the RegattaEntry
 *          type: string
 *        SailNo:
 *          description: Sail No of the RegattaEntry
 *          type: string
 *        Model:
 *          description: Model of the RegattaEntry
 *          type: string
 *        Harbor:
 *          description: Harbor of the RegattaEntry
 *          type: string
 *        Club:
 *          description: Club of the RegattaEntry
 *          type: string
 *        PreliminaryCrewQuantity:
 *          description: Preliminary Crew Quantity of the RegattaEntry
 *          type: integer
 *        CoachName:
 *          description: CoachName of the RegattaEntry
 *          type: string
 *        CoachEmail:
 *          description: CoachEmail of the RegattaEntry
 *          type: string
 *        CoachPhone:
 *          description: CoachPhone of the RegattaEntry
 *          type: string
 *        Content:
 *          description: Content of the RegattaEntry
 *          type: string
 *        Lock:
 *          description: Lock of the RegattaEntry
 *          type: boolean
 *        TrackerId:
 *          description: Tracker ID of the RegattaEntry
 *          type: integer
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaEntryParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - Uid
 *            - RegistrationNo
 *            - Lock
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaEntry
 *              type: string
 *        - $ref: '#/definitions/RegattaEntryParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaEntryQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaEntry:
 *      allOf:
 *        - $ref: '#/definitions/RegattaEntryCreateParams'
 *        - $ref: '#/definitions/RegattaEntryUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaEntry:
 *        allOf:
 *          - $ref: '#/definitions/RegattaEntry'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaEntry
 *                $ref: '#/components/schemas/Regatta'
 *              RegattaGroup:
 *                description: Regatta Group of the RegattaEntry
 *                $ref: '#/components/schemas/RegattaGroup'
 *              Entry:
 *                description: Entry of the RegattaEntry
 *                $ref: '#/components/schemas/Entry'
 *              Ratings:
 *                description: Ratings of the RegattaEntry
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/Rating'
 *              ProtestorRegattaProtests:
 *                description: ProtestorRegattaProtests of the RegattaEntry
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaProtest'
 *              ProtesteeRegattaProtests:
 *                description: ProtesteeRegattaProtests of the RegattaEntry
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaProtest'
 *              RegattaEntryChecks:
 *                description: RegattaEntryChecks of the RegattaEntry
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaEntryCheck'
 *              RegattaEntryCrewmembers:
 *                description: RegattaEntryCrewmembers of the RegattaEntry
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaEntryCrewmember'
 *              RegattaEntryRequirements:
 *                description: RegattaEntryRequirements of the RegattaEntry
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaEntryRequirement'
 *              RegattaEntryTrackPoints:
 *                description: RegattaEntryTrackPoints of the RegattaEntry
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaEntryTrackPoint'
 *              RegattaResultTableEntries:
 *                description: RegattaResultTableEntries of the RegattaEntry
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaResultTableEntry'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  EntryId: null | string;
  RegattaGroupId: null | string;
  RegattaId: string;
};

export type $Params = {
  BowNo: null | string;
  Club: null | string;
  CoachEmail: null | string;
  CoachName: null | string;
  CoachPhone: null | string;
  Content: null | string;
  Harbor: null | string;
  Image: null | string;
  Lock: boolean;
  Model: null | string;
  Name: null | string;
  PreliminaryCrewQuantity: null | number;
  RegistrationNo: string;
  SailNo: null | string;
  TrackerId: null | number;
  Uid: string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Entry?: $Model<'Entry'>;
  ProtesteeRegattaProtests?: Array<$Model<'RegattaProtest'>>;
  ProtestorRegattaProtests?: Array<$Model<'RegattaProtest'>>;
  Ratings?: Array<$Model<'Rating'>>;
  Regatta?: $Model<'Regatta'>;
  RegattaEntryChecks?: Array<$Model<'RegattaEntryCheck'>>;
  RegattaEntryCrewmembers?: Array<$Model<'RegattaEntryCrewmember'>>;
  RegattaEntryRequirements?: Array<$Model<'RegattaEntryRequirement'>>;
  RegattaEntryTrackPoints?: Array<$Model<'RegattaEntryTrackPoint'>>;
  RegattaGroup?: $Model<'RegattaGroup'>;
  RegattaResultTableEntries?: Array<$Model<'RegattaResultTableEntry'>>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  BowNo: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Club: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  CoachEmail: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      isEmail: true,
      notEmpty: true,
    },
  },
  CoachName: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  CoachPhone: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Content: {
    allowNull: true,
    type: Sequelize.TEXT,
    validate: {
      notEmpty: true,
    },
  },
  Harbor: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Image: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Lock: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  Model: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Name: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  PreliminaryCrewQuantity: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  RegistrationNo: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  SailNo: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  TrackerId: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  Uid: {
    allowNull: false,
    type: Sequelize.STRING,
    unique: true,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaEntry'> => {
  const RegattaEntry = stack.define(
    'RegattaEntry',
    fields(Sequelize),
    {
      tableName: 'RegattaEntries',
    },
  );

  RegattaEntry.associate = (models: $ModelClasses) => {
    models.RegattaEntry.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
    models.RegattaEntry.belongsTo(
      models.RegattaGroup,
      {
        as: 'RegattaGroup',
        foreignKey: 'RegattaGroupId',
      },
    );
    models.RegattaEntry.belongsTo(
      models.Entry,
      {
        as: 'Entry',
        constraints: false,
        foreignKey: 'EntryId',
      },
    );
    models.RegattaEntry.belongsToMany(
      models.Rating,
      {
        as: 'Ratings',
        foreignKey: 'RegattaEntryId',
        otherKey: 'RatingId',
        through: models.RegattaEntryRating,
      },
    );
    models.RegattaEntry.hasMany(
      models.RegattaProtest,
      {
        as: 'ProtestorRegattaProtests',
        foreignKey: 'ProtestorRegattaEntryId',
      },
    );
    models.RegattaEntry.hasMany(
      models.RegattaProtest,
      {
        as: 'ProtesteeRegattaProtests',
        foreignKey: 'ProtesteeRegattaEntryId',
      },
    );
    models.RegattaEntry.hasMany(
      models.RegattaEntryCheck,
      {
        as: 'RegattaEntryChecks',
        foreignKey: 'RegattaEntryId',
      },
    );
    models.RegattaEntry.hasMany(
      models.RegattaEntryCrewmember,
      {
        as: 'RegattaEntryCrewmembers',
        foreignKey: 'RegattaEntryId',
      },
    );
    models.RegattaEntry.hasMany(
      models.RegattaEntryRequirement,
      {
        as: 'RegattaEntryRequirements',
        foreignKey: 'RegattaEntryId',
      },
    );
    models.RegattaEntry.hasMany(
      models.RegattaEntryTrackPoint,
      {
        as: 'RegattaEntryTrackPoints',
        foreignKey: 'RegattaEntryId',
      },
    );
    models.RegattaEntry.hasMany(
      models.RegattaResultTableEntry,
      {
        as: 'RegattaResultTableEntries',
        foreignKey: 'RegattaEntryId',
      },
    );
  };

  RegattaEntry.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaEntry;
});
